import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { WeekScheduleEditor } from "@/components/WeekScheduleEditor";

// Formate les 9 chiffres locaux (sans le 0 initial) : "6 12 34 56 78"
function formatFrenchLocalDigits(val) {
  if (!val) return "";
  let digits = val.replace(/\D/g, "");
  // Si l'utilisateur commence par un 0, on le retire automatiquement
  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 9);
  // Regroupement par tranche de 2 après le premier chiffre : 6 12 34 56 78
  if (digits.length <= 1) return digits;
  const first = digits[0];
  const rest = digits.slice(1);
  const parts = rest.match(/.{1,2}/g) || [];
  return `${first} ${parts.join(" ")}`.trim();
}

// Extrait les 9 chiffres locaux depuis une valeur brute existante (+33... ou 0...)
function extractLocalDigits(raw) {
  if (!raw) return "";
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("33")) {
    digits = digits.slice(2);
  }
  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return formatFrenchLocalDigits(digits);
}

const EMPTY = { name: "", email: "", phone: "", active: true, weekly_schedule: null };

export default function Employees() {
  const [employees, setEmployees] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [customSchedule, setCustomSchedule] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => API.get("/employees").then((r) => setEmployees(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openDialog = (emp) => {
    if (emp) {
      setEditing(emp);
      setForm({ 
        name: emp.name, 
        email: emp.email || "", 
        phone: extractLocalDigits(emp.phone || ""), 
        active: emp.active, 
        weekly_schedule: emp.weekly_schedule 
      });
      setCustomSchedule(!!emp.weekly_schedule);
    } else {
      setEditing(null);
      setForm(EMPTY);
      setCustomSchedule(false);
    }
    setOpen(true);
  };

  const save = async () => {
    const rawDigits = form.phone ? form.phone.replace(/\D/g, "") : "";
    
    // Si renseigné, un numéro français requiert 9 chiffres hors préfixe (+33)
    if (rawDigits && rawDigits.length !== 9) {
      toast.error("Numéro de téléphone incomplet (9 chiffres requis après +33)");
      return;
    }

    setSaving(true);
    const payload = { 
      ...form, 
      phone: rawDigits ? `+33${rawDigits}` : "",
      weekly_schedule: customSchedule ? form.weekly_schedule || {} : null 
    };

    try {
      if (editing) await API.put(`/employees/${editing.id}`, payload);
      else await API.post("/employees", payload);
      toast.success(editing ? "Employé modifié" : "Employé créé");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (emp) => {
    try {
      await API.delete(`/employees/${emp.id}`);
      toast.success("Employé supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-slate-900">Employés</h1>
        <Button onClick={() => openDialog(null)} data-testid="btn-new-employee"><Plus className="mr-2 h-4 w-4" /> Nouvel employé</Button>
      </div>

      {employees === null ? (
        <p className="text-sm text-slate-400">Chargement...</p>
      ) : employees.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="employees-empty">
          <p className="text-sm text-slate-500">Aucun employé. Ajoutez vos employés pour leur attribuer des rendez-vous.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {employees.map((emp) => (
            <div key={emp.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`employee-card-${emp.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-display text-sm font-bold text-blue-700">
                  {emp.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="truncate font-display text-base font-semibold text-slate-900">{emp.name}</h3>
                  <p className="truncate text-xs text-slate-500">{emp.email || "—"}</p>
                  {emp.phone && (
                    <p className="truncate text-xs text-slate-400">
                      +33 {extractLocalDigits(emp.phone)}
                    </p>
                  )}
                </div>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${emp.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {emp.active ? "Actif" : "Inactif"}
                </span>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {emp.weekly_schedule ? "Horaires personnalisés" : "Suit les horaires de l'entreprise"}
              </p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openDialog(emp)} data-testid={`btn-edit-employee-${emp.id}`}>
                  <Pencil className="mr-1 h-3 w-3" /> Modifier
                </Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(emp)} data-testid={`btn-delete-employee-${emp.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier l'employé" : "Nouvel employé"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-form-name" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-form-email" className="mt-1" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <div className="mt-1 flex rounded-md border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-slate-900 focus-within:ring-offset-1">
                  <span className="inline-flex items-center rounded-l-md border-r border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-600">
                    🇫🇷 +33
                  </span>
                  <Input 
                    type="tel" 
                    placeholder="6 12 34 56 78" 
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: formatFrenchLocalDigits(e.target.value) })} 
                    data-testid="employee-form-phone" 
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <Label>Employé actif</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="employee-form-active" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <Label>Horaires personnalisés</Label>
                <p className="text-xs text-slate-400">Sinon, l'employé suit les horaires de l'entreprise.</p>
              </div>
              <Switch checked={customSchedule} onCheckedChange={setCustomSchedule} data-testid="employee-form-custom-schedule" />
            </div>
            {customSchedule && (
              <WeekScheduleEditor
                value={form.weekly_schedule || {}}
                onChange={(v) => setForm({ ...form, weekly_schedule: v })}
                testidPrefix="emp-schedule"
              />
            )}
            <Button className="w-full" onClick={save} disabled={!form.name || saving} data-testid="employee-form-submit">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}